import { memo, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Box, TextField, Button, InputAdornment } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'

export const MANAGEMENT_SEARCH_DEBOUNCE_MS = 500

function ManagementSearchBar({
  label,
  placeholder,
  debounceMs = MANAGEMENT_SEARCH_DEBOUNCE_MS,
  defaultSearch = '',
  /** When set, syncs the input from an external source (URL / Clear / Recent). */
  appliedSearch,
  onApplySearchQuery,
  children,
  sx,
  maxWidth = 500,
  mb = 3,
}) {
  const [searchInput, setSearchInput] = useState(() =>
    String(appliedSearch != null ? appliedSearch : defaultSearch || ''),
  )
  const debouncedInput = useDebouncedValue(searchInput, debounceMs)
  const lastCommittedSearchRef = useRef(
    String(appliedSearch != null ? appliedSearch : defaultSearch || ''),
  )
  // Keep latest apply callback in a ref so debounce effect only reacts to
  // debouncedInput changes. Including onApplySearchQuery in the effect deps
  // re-fires while debouncedInput is still stale (e.g. after Clear), which
  // re-applies the previous search and leaves the input empty but data filtered.
  const onApplySearchQueryRef = useRef(onApplySearchQuery)
  onApplySearchQueryRef.current = onApplySearchQuery

  useEffect(() => {
    if (appliedSearch == null) return
    const next = String(appliedSearch)
    if (next === lastCommittedSearchRef.current && next === searchInput) return
    setSearchInput(next)
    lastCommittedSearchRef.current = next
  }, [appliedSearch]) // eslint-disable-line react-hooks/exhaustive-deps -- sync from URL/Clear/Recent only

  useEffect(() => {
    if (debouncedInput === lastCommittedSearchRef.current) return
    lastCommittedSearchRef.current = debouncedInput
    onApplySearchQueryRef.current(debouncedInput)
  }, [debouncedInput])

  const handleSearchChange = useCallback((event) => {
    setSearchInput(event.target.value)
  }, [])

  const commitImmediate = useCallback((value) => {
    lastCommittedSearchRef.current = value
    onApplySearchQueryRef.current(value)
  }, [])

  const handleSearchSubmit = useCallback(
    (event) => {
      event.preventDefault()
      const trimmed = searchInput.trim()
      setSearchInput(trimmed)
      commitImmediate(trimmed)
    },
    [searchInput, commitImmediate],
  )

  const handleSearchClear = useCallback(() => {
    setSearchInput('')
    commitImmediate('')
  }, [commitImmediate])

  const inputProps = useMemo(
    () => ({
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon sx={{ color: '#666' }} />
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end">
          <Button
            type="button"
            size="small"
            onClick={handleSearchClear}
            disabled={!searchInput}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              color: '#666',
              visibility: searchInput ? 'visible' : 'hidden',
            }}
          >
            Clear
          </Button>
        </InputAdornment>
      ),
    }),
    [searchInput, handleSearchClear],
  )

  return (
    <Box sx={{ mb, ...sx }}>
      <form onSubmit={handleSearchSubmit}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            maxWidth,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            label={label}
            value={searchInput}
            onChange={handleSearchChange}
            size="small"
            sx={{ flexGrow: 1, minWidth: 220 }}
            InputProps={inputProps}
            placeholder={placeholder}
          />
          <Button
            type="submit"
            variant="contained"
            sx={{
              bgcolor: '#e90000',
              '&:hover': { bgcolor: '#1B5E20' },
              minWidth: 'auto',
              px: 2,
            }}
          >
            Search
          </Button>
          {children}
        </Box>
      </form>
    </Box>
  )
}

export default memo(ManagementSearchBar)
